import * as React from 'react';
import { toast } from '@grandvisionhq/common';
import { DiscountCodeForm } from '@grandvisionhq/forms';
import { useMutation } from '@grandvisionhq/graphql';
import { useFeatureToggles, useIntl } from '@grandvisionhq/state';
import { isNil } from '@grandvisionhq/utils';
import { useCartStorage } from '../../../hooks/use-cart-storage.js';
import { getUnappliedVoucherCodes } from '../../../utils/get-unapplied-voucher-codes.js';
import { ADD_CART_VOUCHER_CODES } from '../../../operations/mutations/add-cart-voucher-codes.js';
import { GET_CART } from '../../../operations/queries/get-cart.js';

const VoucherCodeFormContainer = ({ inputId , className , 'data-t': dataT ,  })=>{
    const [{ cartId  }] = useCartStorage();
    const { isOmniVoucherEnabled  } = useFeatureToggles();
    const { getLabel  } = useIntl();
    const handleError = React.useCallback(()=>{
        toast.failed('common.error.network');
    }, []);
    const [addCartVoucherCodes] = useMutation(ADD_CART_VOUCHER_CODES, {
        onError: handleError,
        onCompleted (data) {
            var ref, ref1, ref2;
            const newCart = data === null || data === void 0 ? void 0 : data.addCartVoucherCodes.cart;
            const voucherStates = newCart === null || newCart === void 0 ? void 0 : (ref = newCart.vouchers) === null || ref === void 0 ? void 0 : ref.map(({ state  })=>state
            );
            // First check if any voucher codes are not in the state of "MatchesCart"
            // according to: https://docs.commercetools.com/api/projects/carts#discountcodestate
            // Show a warning toast for all voucher states other than MatchesCart
            if (voucherStates === null || voucherStates === void 0 ? void 0 : voucherStates.some((state)=>state != 'MatchesCart'
            )) {
                var ref3;
                newCart === null || newCart === void 0 ? void 0 : (ref3 = newCart.vouchers) === null || ref3 === void 0 ? void 0 : ref3.forEach(({ state  })=>{
                    if (state !== 'MatchesCart') {
                        toast.failed(`cart.validations.discountcode.${state.toLowerCase()}`);
                    }
                });
                return;
            }
            // Check if some vouchers are not applicable to the cart and show toast message
            if (newCart === null || newCart === void 0 ? void 0 : (ref1 = newCart.vouchers) === null || ref1 === void 0 ? void 0 : ref1.length) {
                const unappliedVouchers = getUnappliedVoucherCodes(newCart === null || newCart === void 0 ? void 0 : newCart.vouchers, newCart === null || newCart === void 0 ? void 0 : newCart.appliedDiscounts);
                if (unappliedVouchers === null || unappliedVouchers === void 0 ? void 0 : unappliedVouchers.length) {
                    toast.failed(`cart.validations.discountcode.doesnotmatchcart`);
                    return;
                }
            }
            if ((ref2 = data === null || data === void 0 ? void 0 : data.addCartVoucherCodes.failures) === null || ref2 === void 0 ? void 0 : ref2.length) {
                for (const failure of data.addCartVoucherCodes.failures){
                    if (failure.__typename === 'VoucherCodeNotApplicableFailure') {
                        toast.failed('cart.validations.discountcode.notvalid');
                        return;
                    } else if (failure.__typename === 'UnknownCartMutationFailure') {
                        toast.failed('common.error.network');
                        return;
                    }
                }
            }
            // If no problems fire success toast
            toast.success('cart.validations.discountcode.matchescart');
        }
    });
    const handleSubmit = React.useCallback((inputValue)=>{
        if (!cartId) {
            return;
        }
        addCartVoucherCodes({
            variables: {
                cartInput: {
                    cartId,
                    voucherCodes: [
                        inputValue
                    ]
                }
            },
            update (cache, { data  }) {
                if (data && data.addCartVoucherCodes.success === true) {
                    const newCart = data.addCartVoucherCodes.cart;
                    cache.writeQuery({
                        query: GET_CART,
                        variables: {
                            id: cartId
                        },
                        data: newCart
                    });
                }
            }
        });
    }, [
        cartId,
        addCartVoucherCodes
    ]);
    if (isNil(cartId)) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(DiscountCodeForm, {
        content: {
            inputField: {
                inputId: inputId || 'discount-code',
                type: 'text'
            }
        },
        className: className,
        onSubmit: handleSubmit,
        placeholderLabel: getLabel('cart.discountForm.placeholder'),
        buttonCaptionLabel: getLabel('cart.discountForm.cta'),
        "data-t": dataT,
        showVoucherScanner: isOmniVoucherEnabled
    });
};

export { VoucherCodeFormContainer };
//# sourceMappingURL=VoucherCodeFormContainer.js.map
